import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, FormControlLabel, Checkbox,
  Box, Typography, Stack, Chip, Alert,
} from '@mui/material';
import axios from 'axios';
import { API_URL } from '../../config';

const KNOWN_ENTITLEMENTS = ['explorer', 'resume_gen', 'applications'];
const STATUSES = ['pending', 'active', 'suspended'];

// Edit a single user's status / role / permissions / entitlements.
// PATCHes /api/admin/users/{id} with only the fields the user touched.
const UserEditDialog = ({ open, user, onClose, onSaved }) => {
  const [status, setStatus] = useState(user?.status || 'pending');
  const [role, setRole] = useState(user?.role || 'free');
  const [isAdminGrant, setIsAdminGrant] = useState(
    (user?.permissions || []).includes('admin')
  );
  const [entitlements, setEntitlements] = useState(user?.entitlements || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      setStatus(user.status);
      setRole(user.role);
      setIsAdminGrant((user.permissions || []).includes('admin'));
      setEntitlements(user.entitlements || []);
      setError(null);
    }
  }, [user]);

  if (!user) return null;

  const toggleEntitlement = (name) => {
    setEntitlements((prev) =>
      prev.includes(name) ? prev.filter((e) => e !== name) : [...prev, name]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        status,
        role,
        permissions: isAdminGrant ? ['admin'] : [],
        entitlements,
      };
      const { data } = await axios.patch(
        `${API_URL}/api/admin/users/${user.user_id}`,
        payload
      );
      onSaved && onSaved(data);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit user
        <Typography variant="body2" color="text.secondary">
          {user.email}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            size="small"
            fullWidth
          >
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Role (billing tier)"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            size="small"
            fullWidth
            helperText="Free-form. Used for billing/segmentation. Not access control."
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Permissions</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAdminGrant}
                  onChange={(e) => setIsAdminGrant(e.target.checked)}
                />
              }
              label="Admin (full portal access + view-as + free-form SQL)"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Note: @maxboyd.ai users are admins implicitly via ADMIN_EMAILS env var.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Entitlements</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {KNOWN_ENTITLEMENTS.map((name) => {
                const on = entitlements.includes(name);
                return (
                  <Chip
                    key={name}
                    label={name}
                    color={on ? 'primary' : 'default'}
                    variant={on ? 'filled' : 'outlined'}
                    onClick={() => toggleEntitlement(name)}
                    sx={{ mb: 1, cursor: 'pointer' }}
                  />
                );
              })}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              explorer = explore/roles + explore/companies + preferences. resume_gen = automation + profile enhancement. applications = workflow + tracking.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserEditDialog;
